import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import LogoLink from '../LogoLink';
import identityApi from '../../clients/IdentityClient';
import { CreditPackageProduct } from '../../models/CreditPackageProduct';
import CreditProductList from './CreditProductList';
import UserInputForm from './UserInputForm';
import StripeWrapper from './StripeWrapper';
import { GiftFormState, defaultFormState } from '../../models/GiftFormState';
import { useAuth } from '../../context/AuthContext';

// Declare dataLayer for Google Tag Manager
declare global {
  interface Window {
    dataLayer: any[];
  }
}

const Checkout: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [creditProducts, setCreditProducts] = useState<CreditPackageProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<CreditPackageProduct | null>(null);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [formData, setFormData] = useState<GiftFormState>(defaultFormState);
  const { user } = useAuth();

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      try {
        const products = await identityApi.getCreditProducts();
        const productId = searchParams.get('id');
        const matchedProduct = products.find(p => p.id === productId);

        const creditsNeeded = searchParams.get('credits');
        let productWithEnoughCredits: CreditPackageProduct | undefined;
        if(creditsNeeded){
          const creditsNeededInt = parseInt(creditsNeeded);
          productWithEnoughCredits = products.find(p => p.creditsAmount >= creditsNeededInt) ??
          products.reduce((a, b) => a.creditsAmount > b.creditsAmount ? a : b);
        }

        setCreditProducts(matchedProduct ? [matchedProduct] : products);
        setSelectedProduct(matchedProduct || productWithEnoughCredits || products[0] || null);

        const isGift = searchParams.get('isGift') === 'true';
        setFormData(prev => ({ ...prev,
           isGift, 
           buyerEmail: user?.email || '', 
           buyerFirstName: user?.firstName || '', 
           buyerLastName: user?.lastName || '' }));
      } catch (err) {
        console.error('Failed to load credit products:', err);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [searchParams, user]);

  const handleProductSelect = (product: CreditPackageProduct) => {
    setSelectedProduct(product);
  };

  const handleFormChange = (data: Partial<GiftFormState>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const handlePaymentSuccess = (transactionId: string) => {
    if (selectedProduct && window.dataLayer) {
      window.dataLayer.push({
        'event': 'purchase',
        'transaction_id': transactionId,
        'value': selectedProduct.price,
        'currency': 'USD',
        'items': [{
          'item_name': selectedProduct.title
        }]
      });
    }
    navigate(`/thank-you?isGift=${formData.isGift}`);
  };

  const handlePaymentError = (errorMessage: string) => {
    console.error('Payment error:', errorMessage);
  };

  const handlePaymentStart = () => {
    setFormData(prev => ({ ...prev, isProcessing: true }));
  };

  const handlePaymentEnd = () => {
    setFormData(prev => ({ ...prev, isProcessing: false }));
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const committedEmail = formData.buyerEmail && emailRegex.test(formData.buyerEmail) ? formData.buyerEmail : '';

  function handleBack() {
    if(searchParams.get('returnTo')) {
      navigate('/' + searchParams.get('returnTo'));
    } else {
      navigate(-1);
    }
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <div className="checkout-header">
          <LogoLink className="navbar-brand mb-2" />
        </div>
        <div className='checkout-content'>
          <button
            type="button"
            className="checkout-back-btn"
            onClick={handleBack}
            disabled={formData.isProcessing}
          >
            ← Back
          </button>
          <CreditProductList
            products={creditProducts}
            selectedProduct={selectedProduct}
            onProductSelect={handleProductSelect}
            isLoading={isLoadingProducts}
            disabled={!!clientSecret}
          />

          <div className="checkout-grid">
            <div className="checkout-grid-main">
              <UserInputForm
                selectedProduct={selectedProduct}
                clientSecret={clientSecret}
                onClientSecretChange={setClientSecret}
                formData={formData}
                onFormChange={handleFormChange}
              />
              <StripeWrapper
                clientSecret={clientSecret}
                selectedProduct={selectedProduct}
                buyerName={`${formData.buyerFirstName} ${formData.buyerLastName}`}
                buyerEmail={formData.buyerEmail}
                committedEmail={committedEmail}
                isProcessing={formData.isProcessing}
                isGift={formData.isGift}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                onPaymentStart={handlePaymentStart}
                onPaymentEnd={handlePaymentEnd}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;

